import React from "react";
import { connect } from "react-redux";

import { Dispatch, bindActionCreators } from "redux";
import nameof from "ts-nameof.macro";

import { Dialog, Form, CancelLink, DataTableDef, Text } from "@plex/react-components";

import { INotification } from "infrastructure/notification";
import { Banner } from "ui/app";
import { DialogContainer } from "ui/app/navigation";
import { useLocalizedValues, useLocalizerWithFallback } from "ui/hooks";
import { glossaryMessages } from "ui/screens/plex-dictionary/plex-dictionary.localization";

import { termTranslationsUploadActions } from "./TermTranslationsUpload.actions";
import styles from "./TermTranslationsUpload.module.scss";
import { ITermTranslationsUploadValidationIssue, ITermTranslationsUploadError, ITermTranslationsUploadState } from "./TermTranslationsUpload.state";

export interface ITermTranslationsUploadResult {
  error?: ITermTranslationsUploadError;
  notifications: INotification[];
  resetState: () => void;
}

const getKey = (keyPart: string) => {
  return `termTranslationsUploadResultGrid-${keyPart}`;
};

export const TermTranslationsUploadResult: React.FC<ITermTranslationsUploadResult> = ({ error, notifications, resetState }) => {
  const localizedValues = useLocalizedValues(() => {
    return {
      key: glossaryMessages.property("termTranslationsUploadResultItemKey"),
      locale: glossaryMessages.property("termTranslationsUploadResultItemLocale"),
      industry: glossaryMessages.property("termTranslationsUploadResultItemIndustry"),
      termTranslation: glossaryMessages.property("termTranslationsUploadResultItemTermTranslation"),
      definitionTranslation: glossaryMessages.property("termTranslationsUploadResultItemDefinitionTranslation"),
      validationResult: glossaryMessages.property("termTranslationsUploadResultItemResult"),
      validationResultSectionHeader: glossaryMessages.heading("termTranslationsUploadValidationResults"),
      dialogTitle: glossaryMessages.title("termTranslationsUploadValidationResult")
    };
  });

  const localizeWithFallback = useLocalizerWithFallback();

  return (
    <DialogContainer
      onHidden={resetState}
      render={dialogProps => {
        return (
          <Dialog {...dialogProps} title={localizedValues.dialogTitle}>
            <Dialog.Body>
              <Banner messages={notifications} messageClassName={styles.dialogBanner} />
              {error && (
                <Form onSubmit={() => {}}>
                  <Form.Body>
                    <Form.Section title={localizedValues.validationResultSectionHeader}>
                      <Form.Row>
                        {error.validationIssues ? (
                          <DataTableDef<ITermTranslationsUploadValidationIssue> data={error.validationIssues} keySelector={row => row.uniqueId}>
                            <DataTableDef.Column<ITermTranslationsUploadValidationIssue> id={getKey(nameof(localizedValues.key))} title={localizedValues.key} sortable>
                              {row => <p>{row.key}</p>}
                            </DataTableDef.Column>
                            <DataTableDef.Column<ITermTranslationsUploadValidationIssue> id={getKey(nameof(localizedValues.locale))} title={localizedValues.locale} sortable>
                              {row => <p>{row.locale}</p>}
                            </DataTableDef.Column>
                            <DataTableDef.Column<ITermTranslationsUploadValidationIssue> id={getKey(nameof(localizedValues.industry))} title={localizedValues.industry} sortable>
                              {row => <p>{row.industry}</p>}
                            </DataTableDef.Column>
                            <DataTableDef.Column<ITermTranslationsUploadValidationIssue>
                              id={getKey(nameof(localizedValues.termTranslation))}
                              title={localizedValues.termTranslation}
                              sortable
                            >
                              {row => <p>{row.termTranslation}</p>}
                            </DataTableDef.Column>
                            <DataTableDef.Column<ITermTranslationsUploadValidationIssue>
                              id={getKey(nameof(localizedValues.definitionTranslation))}
                              title={localizedValues.definitionTranslation}
                              sortable
                            >
                              {row => <p>{row.definitionTranslation}</p>}
                            </DataTableDef.Column>
                            <DataTableDef.Column<ITermTranslationsUploadValidationIssue>
                              id={getKey(nameof(localizedValues.validationResult))}
                              title={localizedValues.validationResult}
                              sortable
                            >
                              {row => <p>{localizeWithFallback(row.validationIssue.message)}</p>}
                            </DataTableDef.Column>
                          </DataTableDef>
                        ) : (
                          <Form.FieldPair labelText={localizedValues.validationResult} className={styles.formResultField}>
                            <Text text={localizeWithFallback(error.text)}></Text>
                          </Form.FieldPair>
                        )}
                      </Form.Row>
                    </Form.Section>
                  </Form.Body>
                </Form>
              )}
            </Dialog.Body>
            <Dialog.Footer>
              <CancelLink onClick={dialogProps.onHide}></CancelLink>
            </Dialog.Footer>
          </Dialog>
        );
      }}
    />
  );
};

export default connect(
  (state: { termTranslationsUpload: ITermTranslationsUploadState }) => {
    return {
      error: state.termTranslationsUpload.error,
      notifications: state.termTranslationsUpload.notifications
    };
  },
  (dispatch: Dispatch) => {
    return bindActionCreators(
      {
        resetState: termTranslationsUploadActions.resetUploadingTranslations
      },
      dispatch
    );
  }
)(TermTranslationsUploadResult);
